import moment from 'moment';
import * as flights from '../flights';
import * as api from '../api';
import * as utils from '../utils';

export class FlightNavigationService {
    public getBookingUrl(searchParams: flights.FlightSearchParams, searchId: string, flightId: string): string {
        return this.getSearchResultsUrl(searchParams) + '/booking/' + searchId + '/' + flightId;
    }

    public getBookingUrlNotParsed(search: string, searchId: string, flightId: string): string {
        return '/flights' + search + '/booking/' + searchId + '/' + flightId;
    }

    public getParamsFromRequest(request: api.FlightSearchRequest): flights.FlightSearchParams {
        const result: flights.FlightSearchParams = {
            from: request.sections[0].from,
            to: request.sections[0].to,
            departure: moment(request.sections[0].date).toDate(),
            return: null,
            roundtrip: (request.sections.length > 1),
            class: flights.BookingClass.Economic,
            passengers: {
                adults: request.adults,
                children: request.children,
                infants: request.babies
            }
        };

        if (request.sections.length > 1) {
            result.return = moment(request.sections[1].date).toDate();
        }

        return result;
    }

    public getSearchResultsUrl(searchParams: flights.FlightSearchParams): string {
        let url = '/flights';

        if (searchParams) {
            url += '/' + searchParams.from + '/' + searchParams.to;
            url += '/' + moment(searchParams.departure!).format('MMDD');

            if (searchParams.roundtrip) {
                url += '/' + searchParams.to + '/' + searchParams.from;
                url += '/' + moment(searchParams.return!).format('MMDD');
            }

            url += '/' + searchParams.passengers.adults + '' + searchParams.passengers.children + '' + searchParams.passengers.infants;
            url += '/';

            switch (searchParams.class) {
                case flights.BookingClass.Business:
                    url += 'b';
                    break;
                case flights.BookingClass.Economic:
                    url += 'e';
                    break;
                case flights.BookingClass.EconomicPremium:
                    url += 'ep';
                    break;
                case flights.BookingClass.EconomicTourist:
                    url += 'et';
                    break;
                case flights.BookingClass.First:
                    url += 'f';
                    break;
                default:
                    url += 'e'; // Defaults to Economic
            }
        }

        return url;
    }

    public getOnewayParams(
        from: string,
        to: string,
        date: string,
        passengers: string,
        bookingClass: string
    ): flights.FlightSearchParams | null {
        const result: flights.FlightSearchParams = {
            from: '',
            to: '',
            departure: null,
            return: null,
            roundtrip: true,
            class: flights.BookingClass.Economic,
            passengers: {
                adults: 1,
                children: 0,
                infants: 0
            }
        };

        result.roundtrip = false;

        if (from && from.length === 3) {
            result.from = from.toUpperCase();
        } else {
            return null;
        }

        if (to && to.length === 3) {
            result.to = to.toUpperCase();
        } else {
            return null;
        }

        if (date && date.length === 4) {
            const d = this.parseDate(date);

            if (d) {
                result.departure = d;
                // Set return date the same for UI usage.
                // TODO: strange code. There will be no errors.
                // result.return = d;
            } else {
                return null;
            }
        } else {
            return null;
        }

        if (bookingClass && bookingClass.length >= 1 && bookingClass.length <= 2) {
            result.class = this.parseBookingClass(bookingClass);
        } else {
            return null;
        }

        if (passengers && passengers.length === 3) {
            const nAdults = parseInt(passengers[0]);
            const nChildren = parseInt(passengers[1]);
            const nInfants = parseInt(passengers[2]);

            if (nAdults >= 0
                && nChildren >= 0
                && nInfants >= 0
                && !(nAdults === 0 && nChildren === 0 && nInfants === 0)
            ) {
                if (nAdults + nChildren + nInfants > 9) {
                    // Total limit
                    return null;
                }

                if (nInfants > 0 && nInfants > nAdults && nAdults > 0) {
                    // Infants limit
                    return null;
                }

                result.passengers.adults = nAdults;
                result.passengers.children = nChildren;
                result.passengers.infants = nInfants;
            }
        } else {
            return null;
        }

        return result;
    }

    public getRoundtripParams(
        from: string,
        to: string,
        date: string,
        from2: string,
        to2: string,
        date2: string,
        passengers: string,
        bookingClass: string
    ): flights.FlightSearchParams | null {
        const result = this.getOnewayParams(from, to, date, passengers, bookingClass);

        if (!result) {
            return null;
        }

        result.roundtrip = true;

        if (from2 && from2.length === 3 && from2.toUpperCase() === result.to) {
            // OK
        } else {
            return null;
        }

        if (to2 && to2.length === 3 && to2.toUpperCase() === result.from) {
            // OK
        } else {
            return null;
        }

        if (date2 && date2.length === 4) {
            const d = this.parseDate(date2);

            if (d) {
                result.return = d;
            } else {
                return null;
            }
        } else {
            return null;
        }

        return result;
    }

    /**
     * Additional logics before flight search navigation (search form submit).
     */
    public onBeforeNavigation(searchParams: flights.FlightSearchParams) {
        const markerOwner = utils.Cookies.getMarkerOwner();
        if (markerOwner == '335141' || markerOwner == '335142') {
            utils.Cookies.setMarkerValue(searchParams.from + searchParams.to);
        }
    }

    private parseBookingClass(s: string): string {
        switch (s) {
            case 'b':
                return flights.BookingClass.Business;
            case 'e':
                return flights.BookingClass.Economic;
            case 'ep':
                return flights.BookingClass.EconomicPremium;
            case 'et':
                return flights.BookingClass.EconomicTourist;
            case 'f':
                return flights.BookingClass.First;
            default:
                return flights.BookingClass.Economic; // Defaults to Economic
        }
    }

    private parseDate(stringToParse: string): Date | undefined {
        if (stringToParse.length === 4) {
            let parsed: moment.Moment;

            if (stringToParse === '0229' && !moment().isLeapYear()) {
                parsed = moment(`${moment().year()}0228`, 'YYYYMMDD');
            } else {
                parsed = moment(`${moment().year()}${stringToParse}`, 'YYYYMMDD');
            }

            const today = moment().startOf('day');

            if (parsed.isBefore(today)) {
                if (stringToParse === '0229' && !moment().add(1, 'year').isLeapYear()) {
                    parsed = moment(`${moment().add(1, 'year').year()}0228`, 'YYYYMMDD');
                } else {
                    parsed = moment(`${moment().add(1, 'year').year()}${stringToParse}`, 'YYYYMMDD');
                }
            }

            return moment({ year: parsed.year(), month: parsed.month(), day: parsed.date() }).toDate();
        }

        return undefined;
    }
}
